<template>
  <PageView :main-title="$t('graeList.title')" :no-sub-title-expected="true">
    <ContentView :wide-screen="true">
      <div v-if="canSeeNational()">
        <GraeListVueNational :elements="graesStats"/>
      </div>
      <TableFilterView
          :columns="columns"
          :initial-filters="activeFilters"
          :show-export="false"
          @update-active-filters="updateActiveFilters"
          @update-visible-columns="updateVisibleColumns"
      />
      <b-table
          :ref="tableRef"
          :striped="config.isStriped"
          :hoverable="config.isHoverable"
          :debounce-search="config.searchDebounce"
          :aria-next-label="config.nextLabel()"
          :aria-previous-label="config.previousLabel()"
          :aria-page-label="config.pageLabel()"
          :aria-current-label="config.currentPageLabel()"
          per-page="50"
          :sticky-header="config.stickyHeader"
          :current-page="currentPagedSortedParameter.pageNumber"
          :pagination-position="config.paginationPosition"
          backend-pagination
          :sort-icon="config.sortIcon"
          :sort-icon-size="config.sortIconSize"
          backend-sorting
          sort-multiple
          :sort-multiple-data="buefySortingPriority"
          :total="total"
          :data="graeList"
          :class="{ filtered: activeFilters.length }"
          :loading="isLoading"
          @sort="onSort"
          @sorting-priority-removed="sortingPriorityRemoved"
          @page-change="onPageChange"
      >
        <b-table-column
            v-for="(column, i) in columns"
            :key="i"
            v-bind="column"
            :visible="column.visible"
        >
          <template #header>
            <b-tooltip
                v-if="column.tooltip"
                size="is-small"
                append-to-body
                dashed
                position="is-bottom"
                animated
            >
              {{ column.label }}
              <template #content
              ><span>{{ column.tooltip }}</span></template
              >
            </b-tooltip>
            <span v-else> {{ column.label }}</span>
          </template>
          <template #default="props">
            <div v-if="column.field === 'region'" style="text-align: center">
              <div class="item-label label-container">
                <span>{{ props.row["nom"] }}</span>
                <span v-if="shouldDisplayBadge(props.row['id'])" class="badge-tab">{{
                    getGraeStats(props.row["id"])?.nbCompteAvalider
                  }}</span>
              </div>
            </div>

            <div v-if="column.field === 'ate'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "nbATE") }}
            </div>
            <div v-if="column.field === 'ame'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "nbAME") }}
            </div>
            <div v-if="column.field === 'inscriptions-a-instruire'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "inscriptionsNonInstruites") }}
            </div>
            <div v-if="column.field === 'inscriptions-brouillon'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "inscriptionsBrouillon") }}
            </div>
            <div v-if="column.field === 'labellisations-instruites'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "labellisationsInstructionTerminee") }}
            </div>
            <div v-if="column.field === 'labellisations-a-instruire'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "labellisationsAttenteInstructeur") }}
            </div>
            <div v-if="column.field === 'projets-pause'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "pause") }}
            </div>
            <div v-if="column.field === 'projets-arretes'" style="text-align: center">
              {{ getTableGraeStats(props.row["id"], "arrete") }}
            </div>
            <div v-if="column.field === 'notes' && !isRegional">
              <b-button
                  type="is-outlined"
                  icon-left="comment-alt"
                  @click="openNotesDialog($event, props.row)"
              ></b-button>
            </div>
            <div
                v-if="!column.custom"
                class="clickable"
                @click="
                showDetailsPage(
                  $event,
                  props.row['id'],
                  getGraeStats(props.row['id'])?.nbCompteAvalider || 0,
                )
              "
            >
              <div>{{ props.row[column.field] }}</div>
            </div>
          </template>
        </b-table-column>

        <template #empty>
          <div class="empty-table">
            {{ $t("graeList.aucun-resultat") }}
          </div>
        </template>
      </b-table>
      <NotesModale
          :grae="selectedGrae"
          :open="openNotesModale"
          @close-modale="openNotesModale = false"
          @grae-updated="updateGraeNotes"
      />
    </ContentView>
  </PageView>
</template>

<script lang="ts">
import {defineComponent} from "vue"
import {FilterDTO, Grae, GraeDTO, GraeStatsDTO, PaginationOrder, UtilisateurDTO,} from "@/model/bean/GeneratedDTOs"
import {OrganismeService} from "@/services/rest/organisme/OrganismeService"
import GraeListVueNational from "@/pages/grae/GraeListVueNational.vue"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import Column from "@/views/tables/Column"
import NotesModale from "./NotesModale.vue"
import {StatService} from "@/services/rest/stats/StatService"
import {LoginService} from "@/services/rest/login/LoginService"
import {useTableView} from "@/hooks/useTableView"
import {BButton, BTable, BTableColumn, BTooltip} from "buefy"
import {PROFIL_UTILISATEUR} from "@/model/bean/EnumHelper"
import {UsersService} from "@/services/rest/users/UsersService"

export default defineComponent({
  name: "GRAEListPage",
  components: {
    PageView,
    ContentView,
    TableFilterView,
    NotesModale,
    GraeListVueNational,
    BButton,
    BTooltip,
    BTable,
    BTableColumn,
  },
  data() {
    return {
      organismeService: OrganismeService.INSTANCE,
      statService: StatService.INSTANCE,
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,

      // Propriétés spécifiques à cette classe
      graeList: [] as Array<Grae>,
      graesStats: new Map<string, GraeStatsDTO>(),
      selectedGrae: new GraeDTO(),
      openNotesModale: false,
      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      isRegional: false as boolean | undefined,
      instructedGraes: [] as string[],
      role: "" as string | undefined,

      // Utilisation du hook useTableView - initialisé dans created()
      tableView: null as any,
    }
  },
  computed: {
    // Hook state as computed properties
    tableRef(): string {
      return this.tableView?.state.tableRef || "table"
    },

    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },

    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },

    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter || {}
    },

    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },

    total(): number {
      return this.tableView?.state.total || 0
    },

    config(): any {
      return this.tableView?.state.config || {}
    },

    activeFilters(): Array<FilterDTO> {
      return this.tableView?.state.activeFilters || []
    },

    // Computed property to get grae stats safely for a given row
    getGraeStats() {
      return (rowId: string): GraeStatsDTO | null => {
        return this.graesStats.get(rowId) || null
      }
    },
  },
  created(): void {
    // Initialize hook with proper binding
    this.tableView = useTableView({
      tableRef: "table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })
  },
  mounted(): void {
    this.init()
    this.loadStats()
    this.tableView?.initMounted()
  },
  methods: {
    // Méthodes du hook useTableView
    updateActiveFilters(activeFilters: Array<FilterDTO>): void {
      this.tableView?.updateActiveFilters(activeFilters)
    },

    updateVisibleColumns(columns: Array<Column>): void {
      this.tableView?.updateVisibleColumns(columns)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: "asc" | "desc", _event?: any): void {
      // L'événement @sort de Buefy envoie field, order, event
      // Le hook useTableView attend field et order (string)
      if (field && this.tableView?.onSort) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    // Méthodes spécifiques à cette classe
    init(): void {
      this.loggedUser = this.loginService.getLoggedUser()
      if (this.loggedUser && this.loggedUser.instructedGraes) {
        this.instructedGraes = this.loggedUser.instructedGraes
      }
      this.role = this.loggedUser ? this.loggedUser.profil : ""
      this.isRegional =
          this.usersService.hasAnyProfile(this.loggedUser, [
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          ]) && this.instructedGraes.length !== 0
    },

    async loadStats(): Promise<void> {
      let stats: GraeStatsDTO[]
      if (this.loggedUser && this.isRegional && this.loggedUser.grae) {
        const promises = []
        for (const instructedGrae of this.instructedGraes) {
          promises.push(this.statService.graeStats(instructedGrae))
        }

        promises.push(this.statService.graeStats(this.loggedUser.grae.id))
        stats = await Promise.all(promises)
      } else {
        stats = await this.statService.graesStats()
      }
      this.graesStats = stats.reduce(
          (acc, curr) => acc.set(curr.graeId, curr),
          new Map<string, GraeStatsDTO>(),
      )
    },

    async reloadData(): Promise<void> {
      this.currentPagedSortedParameter.pageSize = 50
      const activeFilters = await this.getActiveFiltersForApis()

      try {
        const paginatedGraeList = await this.organismeService.getGraes(
            this.currentPagedSortedParameter,
            activeFilters,
        )
        this.graeList = paginatedGraeList.elements
        this.updateTotal(paginatedGraeList.count)
      } catch (e) {
        // Silent catch
      }
    },

    async getActiveFiltersForApis(): Promise<FilterDTO[]> {
      //We want a copy of activeFilters
      const activeFilters = this.activeFilters.map((filter) => filter)
      if (this.isRegional && this.loggedUser && this.loggedUser.grae) {
        const graeFilter = new FilterDTO()
        graeFilter.property = "region"

        const allGraes = await this.organismeService.getAllGraes()

        graeFilter.values = [
          ...this.instructedGraes.map((instructedGrae) => {
            const foundGrae = allGraes.find((g) => g.id === instructedGrae)
            return foundGrae ? foundGrae.region : ""
          }),
        ]
        graeFilter.values.push(this.loggedUser.grae.region)

        activeFilters.push(graeFilter)
      }
      return activeFilters
    },

    canSeeNational(): boolean {
      return this.usersService.hasAnyProfile(this.loggedUser, [
        PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
        PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      ])
    },

    getInitialColumns(): Array<Column> {
      // Re-init in case getInitialColumns goes before the created
      //this.init()
      const initialColumns = [
        {
          field: "region",
          label: this.$t("graeList.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "ate",
          label: this.$t("graeList.ate").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "ame",
          label: this.$t("graeList.ame").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "inscriptions-a-instruire",
          label: this.$t("graeList.inscriptions-a-instruire").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "inscriptions-brouillon",
          label: this.$t("graeList.inscriptions-brouillon").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "labellisations-instruites",
          label: this.$t("graeList.labellisations-instruites").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "labellisations-a-instruire",
          label: this.$t("graeList.labellisations-a-instruire").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projets-pause",
          label: this.$t("graeList.projets-pause").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projets-arretes",
          label: this.$t("graeList.projets-arretes").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]

      if (!this.isRegional) {
        initialColumns.push({
          field: "notes",
          label: this.$t("graeList.notes").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }

      return initialColumns
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "region"
      defaultSort.desc = false
      return [defaultSort]
    },

    showDetailsPage(event: Event, id: string, pendingRequest: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      if (pendingRequest) {
        this.$router.push({
          path: "/grae/indicateurs/0/" + id,
        })
      } else {
        this.$router.push("/grae/indicateurs/0/" + id)
      }
    },

    openNotesDialog(event: Event, grae: GraeDTO): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.selectedGrae = grae
      this.openNotesModale = true
    },

    shouldDisplayBadge(id: string): boolean {
      const stats = this.getGraeStats(id)
      if (stats) {
        const pendingRequest = stats.nbCompteAvalider
        if (this.role === "ACCOMPAGNATEUR_NATIONAL" && pendingRequest) {
          return pendingRequest > 0
        }
      }
      return false
    },

    updateGraeNotes(updatedGrae: GraeDTO): void {
      const index = this.graeList.findIndex((g) => g.id === updatedGrae.id)
      if (index !== -1) {
        this.graeList[index].note = updatedGrae.note
        if (updatedGrae.dateModification) {
          this.graeList[index].dateModification = updatedGrae.dateModification
        }
      }
    },

    getTableGraeStats(rowId: string, propertyName: string): number {
      const stats = this.getGraeStats(rowId)
      if (stats && stats[propertyName as keyof GraeStatsDTO] !== undefined) {
        return Number(stats[propertyName as keyof GraeStatsDTO]) || 0
      }
      return 0
    },
  },
})
</script>

<style lang="scss" scoped>
.b-table .table-wrapper.has-sticky-header {
  overflow-y: unset !important;
}

:deep(.table td .button.is-outlined) {
  color: #363636;
  background-color: white;
  border-color: #dbdbdb;
  padding-left: 1em;
  padding-right: 1em;

  .icon.is-small {
    height: 1.5em !important;
    width: 1.5em !important;
  }
}

.label-container {
  position: relative;
  display: flex;
  align-items: center;
}

.badge-tab {
  background-color: #c00000;
  color: white;
  border-radius: 50%;
  font-size: 0.7em;
  width: 16px;
  height: 16px;
  position: absolute;
  justify-content: center;
  align-items: center;
  align-content: center;
  top: -4px;
  right: -10px;
}
</style>
